import { useEffect, useCallback } from "react";

type ImportFactory = () => Promise<unknown>;

const prefetched = new Set<string>();

/**
 * Prefetches a lazy-loaded route chunk via Vite's dynamic import.
 *
 * @param key       Stable string identifier for deduplication (e.g. "booking-calendar").
 *                  Must be unique per chunk — using the same key for different chunks will
 *                  silently skip the second prefetch.
 * @param importFn  The same dynamic import factory used in App.tsx for that route.
 * @param idleDelayMs  When provided, schedules an automatic prefetch during the browser's
 *                  first idle period (requestIdleCallback, falling back to setTimeout on
 *                  Safari).  This covers mobile users who never trigger hover events.
 *
 * Returns an `onMouseEnter` / `onFocus` handler.  Attach it to primary CTA links so the
 * chunk is requested the moment the user moves toward clicking — before they actually click.
 */
export function usePrefetchRoute(
  key: string,
  importFn: ImportFactory,
  idleDelayMs?: number,
): () => void {
  const prefetchOnce = useCallback(() => {
    if (prefetched.has(key)) return;
    prefetched.add(key);
    importFn().catch(() => {});
  }, [key, importFn]);

  useEffect(() => {
    if (idleDelayMs === undefined) return;
    if (prefetched.has(key)) return;

    if ("requestIdleCallback" in window) {
      const id = window.requestIdleCallback(prefetchOnce, {
        timeout: idleDelayMs,
      });
      return () => window.cancelIdleCallback(id);
    } else {
      const id = setTimeout(prefetchOnce, idleDelayMs);
      return () => clearTimeout(id);
    }
  }, [key, idleDelayMs, prefetchOnce]);

  return prefetchOnce;
}
